import { Column, ColumnBodyOptions, ColumnEditorOptions, ColumnEvent } from 'primereact/column';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ElectricityEnum, TypeOfElectricityMixEnum } from 'services/EpdClient';
import { useCreateEpdElectricity, useUpdateEpdElectricity } from 'services/api/mutations';
import { useDictionaryValues } from 'services/api/queries';
import { HeaderCaptionSemiBoldNormalCase } from 'styles/v2/Styles.styled';
import { ElectricityDataModel, EnergySourceModel } from 'types/types';

import { cellNumberEditor } from '../GridCellEditors';
import { validatedBodyTemplateWithPlaceholder } from '../GridService';
import { DataTableStyled, Placeholder } from '../StyledGrid';
import { getOptionsFromDictionary, isPositiveDecimal } from 'util/utils';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import WizardTextAreaComponent from 'components/v2/epd-wizard-inputs/WizardTextArea';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import TooltipErrorIcon from 'components/v2/icons/TooltipErrorIcon';

type TRow = {
  key: number;
  field: ElectricityEnum;
  fieldName: React.ReactNode;
  source: string | undefined;
  value: string | number | EnergySourceModel[] | null | undefined;
};

type TProps = {
  epdVersionId: string;
  headerText: string;
  data: ElectricityDataModel;
  errors: any;
  isPcrV2: boolean;
  isReadOnly: boolean;
};

const ElectricityGrid = ({ epdVersionId, headerText, data, errors, isPcrV2, isReadOnly }: TProps) => {
  const electricityTable = useRef<any>(null);
  const { t } = useTranslation();
  const createElectricityMutation = useCreateEpdElectricity(epdVersionId);
  const updateElectricityMutation = useUpdateEpdElectricity(epdVersionId);

  const dictionariesQuery = useDictionaryValues();
  const typeOfElectricityMixRawOptions = getOptionsFromDictionary(
    dictionariesQuery.data,
    EPD_DICTIONARY_CODES.TYPE_OF_ELECTRICITY_MIX
  );

  const typeOfElectricityMixOptions = isPcrV2
    ? typeOfElectricityMixRawOptions.filter((x) => x.value !== TypeOfElectricityMixEnum.ResidualOfSupplierOnMarket)
    : typeOfElectricityMixRawOptions;

  const rowClassName = () => ({ 'p-disabled': isReadOnly });

  // ToDo: refactor this.
  // Primereact Datatable doesn't support both rowspan and colspan column at the same time for content rows.
  // Rowspan is configured in Datatable, that why colspan is set on every rendering of the Electricity table
  useEffect(() => {
    if (electricityTable.current) {
      const table = electricityTable.current.getTable();
      const tbody = table.getElementsByTagName('tbody');
      const rows = tbody[0].getElementsByTagName('tr');
      if (rows.length >= 13) {
        setColSpan(rows, 0);
        setColSpan(rows, rows.length - 1);
      }
    }
  });

  const setColSpan = (rows: any, rowIndex: number) => {
    const cells = rows[rowIndex].getElementsByTagName('td');
    cells[1].style = 'display: none';
    cells[cells.length - 1].colSpan = 2;
  };

  const getColumnsData = (data: ElectricityDataModel | undefined) => {
    let index = 0;
    const columns = [
      {
        key: index,
        field: ElectricityEnum.TypeOfElectricityMix,
        fieldName: 'Type of electricity mix',
        value: data?.typeOfElectricityMix,
      },
    ] as TRow[];

    data?.energySources?.forEach((item) => {
      columns.push({
        key: ++index,
        field: ElectricityEnum.EnergySource,
        fieldName: 'Energy sources',
        source: t(`epdEnergySource.${item.source}`),
        value: item.value?.toString(),
      });
    });

    columns.push({
      key: ++index,
      field: ElectricityEnum.ClimateImpact,
      fieldName: (
        <>
          GWP-GHG intensity (kg CO<sub>2</sub> eq./kWh):
        </>
      ),
      value: data?.climateImpact,
    } as TRow);

    return columns;
  };

  const createOrUpdateElectricity = (data: ElectricityDataModel) => {
    if (!data) return;
    if (!data.id) {
      createElectricityMutation.mutate(data);
      return;
    }
    updateElectricityMutation.mutate(data);
  };

  const onCellEditComplete = (e: ColumnEvent) => {
    if (!data) {
      return;
    }

    let { rowData, newValue, originalEvent: event } = e;

    switch (rowData.field) {
      case ElectricityEnum.EnergySource:
        if (isPositiveDecimal(newValue)) {
          const newData = {
            ...data,
            energySources: data.energySources?.map((s) => {
              if (rowData.source === t(`epdEnergySource.${s.source}`)) {
                return {
                  ...s,
                  value: Math.min(100, newValue),
                };
              }
              return s;
            }),
          };
          createOrUpdateElectricity(newData);
        }
        break;
      case ElectricityEnum.ClimateImpact:
        if (isPositiveDecimal(newValue)) {
          const newData = {
            ...data,
            climateImpact: newValue,
          };

          createOrUpdateElectricity(newData);
        } else {
          event.preventDefault();
        }

        break;
    }
  };

  const cellSelectEditor = (options: ColumnEditorOptions) => (
    <Dropdown
      style={{ width: '100%', height: '28px', minWidth: '1rem' }}
      value={options.rowData[options.field]}
      optionLabel="label"
      optionValue="value"
      options={typeOfElectricityMixOptions}
      onChange={(e: DropdownChangeEvent) => {
        const newData = { ...data };
        newData.typeOfElectricityMix = e.value;
        createOrUpdateElectricity(newData);
      }}
    />
  );

  const handleElectricityTextChange = (propertyName: string, value: string) => {
    if (!data) return;

    const newData = {
      ...data,
      [propertyName]: value,
    };

    createOrUpdateElectricity(newData);
  };

  const cellEditor = (options: ColumnEditorOptions) => {
    switch (options.rowData.field) {
      case ElectricityEnum.TypeOfElectricityMix:
        return cellSelectEditor(options);
      case ElectricityEnum.EnergySource:
      case ElectricityEnum.ClimateImpact:
        return cellNumberEditor(options);
    }
  };

  const header = (
    <div className="flex flex-wrap align-items-center gap-2">
      <HeaderCaptionSemiBoldNormalCase className="white-space-nowrap">{headerText}</HeaderCaptionSemiBoldNormalCase>
      {[errors?.typeOfElectricityMix, errors?.energySources, errors?.climateImpact].some((error: any) => !!error) && (
        <TooltipErrorIcon content={'Required'} />
      )}
    </div>
  );

  const valueBodyTemplate = (rowData: TRow) => {
    switch (rowData.field) {
      case ElectricityEnum.TypeOfElectricityMix:
        const typeOfElectricityMixLabel = typeOfElectricityMixOptions.find(
          (x) => Number(x.value) === data?.typeOfElectricityMix
        )?.label;
        return typeOfElectricityMixLabel ? (
          typeOfElectricityMixLabel
        ) : (
          <Placeholder>Select how the electricity has been modelled</Placeholder>
        );
      case ElectricityEnum.EnergySource:
        const sourceValue = rowData.value as number | undefined;
        return sourceValue ? `${sourceValue}%` : <Placeholder>Type value %</Placeholder>;
      case ElectricityEnum.ClimateImpact:
        const numberValue = rowData.value as number | undefined;
        return numberValue ? (
          <>
            {numberValue} kg CO<sub>2</sub> eq./kWh
          </>
        ) : (
          <Placeholder>
            Type value, kg CO<sub>2</sub> eq./kWh
          </Placeholder>
        );
      default:
        const stringValue = rowData.value as string | undefined;
        return stringValue && stringValue?.length > 0 ? stringValue : <Placeholder>Type value</Placeholder>;
    }
  };

  const cellTemplate = (value: any, options: ColumnBodyOptions) => {
    return validatedBodyTemplateWithPlaceholder((value as any)?.[options?.field]);
  };

  return (
    <>
      {!data.isDefault && (
        <WizardTextInputComponent
          name="electricityUsedInProcessName"
          value={data?.electricityUsedInProcessName}
          type="text"
          label="Electricity used in"
          placeholder="Unnamed process"
          required
          onChanged={handleElectricityTextChange}
          error={errors?.electricityUsedInProcessName}
        />
      )}
      <DataTableStyled
        header={header}
        value={getColumnsData(data)}
        dataKey="key"
        showGridlines
        editMode="cell"
        rowGroupMode="rowspan"
        groupRowsBy="fieldName"
        ref={electricityTable}
        rowClassName={rowClassName}
      >
        <Column field="fieldName" headerStyle={{ display: 'none' }} style={{ verticalAlign: 'top' }} body={cellTemplate} />
        <Column field="source" headerStyle={{ display: 'none' }} style={{ width: '36%' }} />
        <Column
          field="value"
          body={valueBodyTemplate}
          editor={isReadOnly ? undefined : cellEditor}
          onCellEditComplete={onCellEditComplete}
          headerStyle={{ display: 'none' }}
          style={{ width: '36%' }}
        />
      </DataTableStyled>
      {isPcrV2 && (
        <WizardTextAreaComponent
          name="residualElectricityMixCalculationMethod"
          label="Method used to calculate residual electricity mix"
          placeholder="Type here"
          tooltip="Only applicable if the type of electricity mix is ‘Residual electricity mix on the market’."
          value={data?.residualElectricityMixCalculationMethod}
          onChanged={handleElectricityTextChange}
        />
      )}
    </>
  );
};

export default ElectricityGrid;
